import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { isVehicleContactConnection } from "@/lib/utils/connections/vehicle-contact-connections";
import { getThirdPartyMatterContactIds } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const useVehiclesViewModel = (matterId: string) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const clientVehicleContacts = useMemo(
    () =>
      getMatterContactConnections(matter).filter(
        (connection) => connection.label === "Client",
      ),
    [matter],
  );

  const clientVehicleContactIds = useMemo(
    () => new Set(clientVehicleContacts.map((contact) => contact.contactId)),
    [clientVehicleContacts],
  );

  const clientVehicles = useMemo(
    () =>
      matter.vehicles.filter((vehicle) =>
        vehicle.connections.some(
          (connection) =>
            isVehicleContactConnection(connection) &&
            clientVehicleContactIds.has(connection.targetObjectId),
        ),
      ),
    [matter.vehicles, clientVehicleContactIds],
  );

  const thirdPartyContactIds = useMemo(
    () => getThirdPartyMatterContactIds(matter),
    [matter],
  );

  const defendantVehicles = useMemo(
    () =>
      matter.vehicles.filter((vehicle) =>
        vehicle.connections.some(
          (connection) =>
            isVehicleContactConnection(connection) &&
            thirdPartyContactIds.has(connection.targetObjectId),
        ),
      ),
    [matter.vehicles, thirdPartyContactIds],
  );

  return {
    vehicles: matter.vehicles,
    clientVehicles,
    clientVehicleConnections: clientVehicles.flatMap(
      (vehicle) => vehicle.connections,
    ),
    defendantVehicles,
    defendantVehicleConnections: defendantVehicles.flatMap(
      (vehicle) => vehicle.connections,
    ),
  };
};
